import { useMemo } from "react";
import { useGetList } from "ra-core";
import type { Company, Contact } from "@/components/atomic-crm/types";
import { pickBillToContactId } from "../../../supabase/functions/_shared/billToContactPolicy.ts";

/**
 * Contact an invoice is billed to for the chosen recipients — same rule as
 * the send flow: an account contact who owns a recipient address, else the
 * account alone (null), never the ticket requester by default.
 */
export const useBillToContact = ({
  company,
  contact,
  recipients,
}: {
  company?: Company | null;
  contact?: Contact | null;
  recipients: string[];
}): Contact | null => {
  const { data: accountContacts = [] } = useGetList<Contact>(
    "contacts",
    {
      filter: { "company_id@eq": company?.id },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: company?.id != null, staleTime: 30_000 },
  );
  return useMemo(() => {
    const candidates = company ? accountContacts : contact ? [contact] : [];
    const id = pickBillToContactId({
      recipients,
      candidates,
      defaultContactId: contact?.id,
      hasAccount: Boolean(company),
    });
    if (id == null) return null;
    return (
      candidates.find((row) => String(row.id) === String(id)) ??
      (contact && String(contact.id) === String(id) ? contact : null)
    );
  }, [accountContacts, company, contact, recipients]);
};
